import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { NgxPaginationModule } from 'ngx-pagination';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { ServiceActivity } from '../../../../core/models/serviceactivity.interface';
import { AssistanceService } from '../../../../core/models/assistanceservice.interface';
import { MatDialog } from '@angular/material/dialog';
import { ServiceactivityService } from '../../../../core/services/serviceactivity/serviceactivity-service';
import { ToastrService } from 'ngx-toastr';
import { ServiceactivityForm } from '../serviceactivity-form/serviceactivity-form';
import { ItemSelectorResult } from '../../../../core/models/selectordialog.interface';
import { ItemSelectorDialog } from '../../../../shared/components/item-selector-dialog/item-selector-dialog';
import { AssignConsumableAsset } from '../../../../core/models/consumableasset.interface';
import { ConsumableassetService } from '../../../../core/services/consumableasset/consumableasset-service';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { TermPipe } from '../../../../shared/pipes/term.pipe';
import { TermKey } from '../../../../core/models/terminology.interface';

@Component({
  selector: 'app-assistanceservice-serviceactivity-list',
  imports: [
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgxPaginationModule,
    MatNativeDateModule,
    MatMenuModule,
    MatDividerModule,
    TermPipe,
  ],
  templateUrl: './assistanceservice-serviceactivity-list.html',
  styleUrl: './assistanceservice-serviceactivity-list.scss',
})
export class AssistanceserviceServiceactivityList {
  @Input({ required: true }) service!: AssistanceService;
  @Output() changed = new EventEmitter<void>();

  readonly activitiesLimit = 5;
  activityPage = 1;
  termKey = TermKey;
  activityColumns: string[] = [
    'acciones',
    'id',
    'name',
    'description',
    'month',
    'consumableAssets',
    'locations',
    'adress',
    'line',
    'total',
  ];

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private serviceactivityService: ServiceactivityService,
    private consumableassetService: ConsumableassetService,
  ) {}

  updateActivity(activity: ServiceActivity): void {
    this.serviceactivityService.setServiceActivity(activity);
    const dialogRef = this.dialog.open(ServiceactivityForm, {
      width: '600px',
      height: 'auto',
      maxHeight: '80vh',
      disableClose: true,
      panelClass: 'custom-dialog-container',
      data: { isCreate: false },
    });
    dialogRef.afterClosed().subscribe(() => this.changed.emit());
  }

  deleteActivity(activity: ServiceActivity): void {
    if (!confirm('¿Seguro que deseas eliminar la actividad de servicio?')) return;
    this.serviceactivityService.delete(activity.id).subscribe({
      next: () => {
        this.toastr.success('Eliminado CORRECTAMENTE!', 'Exitoso!', {
          progressBar: true,
          timeOut: 5000,
        });
        this.changed.emit();
      },
      error: () => {
        this.toastr.error('NO se pudo eliminar!', 'Error!', {
          progressBar: true,
          timeOut: 5000,
        });
      },
    });
  }

  openInsumosDialog(serviceActivityId: number): void {
    this.serviceactivityService.getById(serviceActivityId).subscribe((full) => {
      const itemsIniciales = Array.from(
        new Map(
          full.consumableAssets.flatMap((asset) =>
            asset.consumableAsset.map((ca) => [
              ca.id,
              {
                id: ca.id,
                name: ca.name,
                unitValue: asset.expense?.value ?? 0,
                qty: asset.expense?.qty ?? 0,
                unity: asset.expense?.unity ?? 'N/A',
                package: asset.expense?.package ?? 0,
                assignmentId: asset.id,
                exists: true,
              },
            ]),
          ),
        ).values(),
      );
      const assignmentByConsumableId = new Map<number, number>(
        itemsIniciales.map((item) => [Number(item.id), item.assignmentId]),
      );

      const dialogRef = this.dialog.open(ItemSelectorDialog, {
        width: '1000px',
        maxWidth: '95vw',
        maxHeight: '80vh',
        disableClose: true,
        data: {
          title: 'Insumos',
          labelSearch: 'Insumos',
          labelItem: 'Insumos',
          labelValue: 'Valor',
          itemsIniciales,
          showUnity: true,
          showPackage: true,
          uploadItems: () =>
            this.consumableassetService.getConsumableAssets().pipe(
              map((assets) =>
                assets.map((asset) => ({
                  id: asset.id,
                  name: asset.name,
                  package: asset.package,
                  unity: asset.unity,
                  unitValue: asset.value,
                })),
              ),
            ),
        },
      });

      dialogRef.afterClosed().subscribe((result: ItemSelectorResult | undefined) => {
        if (!result) return;
        const finalItems = result.items ?? [];
        const finalConsumableIds = new Set(finalItems.map((it) => Number(it.id)));

        const upsertRequests = finalItems.map((item) => {
          const consumableAssetId = Number(item.id);
          const payload: AssignConsumableAsset = {
            serviceActivity: serviceActivityId,
            consumableAssets: [consumableAssetId],
            value: item.unitValue,
            qty: item.qty,
            unity: item.unity ?? 'N/A',
            package: item.package ?? 0,
          };
          const assignmentId = assignmentByConsumableId.get(consumableAssetId);
          const request$ =
            assignmentId !== undefined
              ? this.consumableassetService.updateAddConsumableAsset(assignmentId, {
                  ...payload,
                  id: assignmentId,
                })
              : this.consumableassetService.addConsumableAsset(payload);
          return request$.pipe(
            map(() => true),
            catchError(() => of(false)),
          );
        });

        const deleteRequests = [...assignmentByConsumableId.entries()]
          .filter(([consumableId]) => !finalConsumableIds.has(consumableId))
          .map(([, assignmentId]) =>
            this.consumableassetService.deleteAssignConsumableAsset(assignmentId).pipe(
              map(() => true),
              catchError(() => of(false)),
            ),
          );

        this.runAssignmentRequests([...upsertRequests, ...deleteRequests], () =>
          this.changed.emit(),
        );
      });
    });
  }

  private runAssignmentRequests(requests: Observable<boolean>[], onDone: () => void): void {
    if (requests.length === 0) {
      onDone();
      return;
    }
    forkJoin(requests).subscribe((res) => {
      const exitosos = res.filter(Boolean).length;
      if (exitosos === res.length) {
        this.toastr.success('Se guardaron los cambios correctamente', 'Exitoso!', {
          progressBar: true,
          timeOut: 5000,
        });
      } else if (exitosos > 0) {
        this.toastr.warning(`Se aplicaron ${exitosos} de ${res.length} cambios`, 'Parcial', {
          progressBar: true,
          timeOut: 5000,
        });
      } else {
        this.toastr.error('No se pudieron guardar los cambios', 'Error!', {
          progressBar: true,
          timeOut: 5000,
        });
      }
      onDone();
    });
  }
}
